/*
 * REAL profile store for signed-in users: Cognito user attributes on the
 * consumer pool, read/written directly with the session access token
 * (cognito-idp JSON API supports browser CORS). No invented backend.
 */
import { readAuthConfig } from './authConfig'

export interface CognitoProfile {
  email?: string
  givenName?: string
  familyName?: string
  birthdate?: string
  gender?: string
  phone?: string
  /** Cognito standard `address` attribute — street address (wizard step 3). */
  address?: string
  /** Cognito standard `locale` attribute — BCP-47 code for the account
   *  language (wizard step 5 maps display labels ↔ codes). */
  locale?: string
}

const TARGETS = {
  get: 'AWSCognitoIdentityProviderService.GetUser',
  update: 'AWSCognitoIdentityProviderService.UpdateUserAttributes',
}

function endpoint(): string {
  const config = readAuthConfig()
  if (!config) throw new Error('auth not configured')
  return `https://cognito-idp.${config.region}.amazonaws.com/`
}

async function call(target: string, body: unknown, fetchImpl: typeof fetch = fetch): Promise<unknown> {
  const res = await fetchImpl(endpoint(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-amz-json-1.1', 'X-Amz-Target': target },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new Error(`cognito ${target.split('.')[1]} failed (${res.status}): ${detail.slice(0, 160)}`)
  }
  return res.json()
}

type AttrList = Array<{ Name: string; Value: string }>

export function attrsToProfile(attrs: AttrList): CognitoProfile {
  const get = (n: string) => attrs.find((a) => a.Name === n)?.Value
  return {
    email: get('email'),
    givenName: get('given_name'),
    familyName: get('family_name'),
    birthdate: get('birthdate'),
    gender: get('gender'),
    phone: get('phone_number'),
    address: get('address'),
    locale: get('locale'),
  }
}

export function profileToAttrs(p: CognitoProfile): AttrList {
  const out: AttrList = []
  if (p.givenName !== undefined) out.push({ Name: 'given_name', Value: p.givenName })
  if (p.familyName !== undefined) out.push({ Name: 'family_name', Value: p.familyName })
  if (p.birthdate !== undefined) out.push({ Name: 'birthdate', Value: p.birthdate })
  if (p.gender !== undefined) out.push({ Name: 'gender', Value: p.gender })
  if (p.phone !== undefined) out.push({ Name: 'phone_number', Value: p.phone })
  if (p.address !== undefined) out.push({ Name: 'address', Value: p.address })
  if (p.locale !== undefined) out.push({ Name: 'locale', Value: p.locale })
  return out
}

/** E.164 normalisation per the design CLAUDE.md: strip spaces/dashes, strip a
 * pasted trunk zero after the dial code (KEEPS_TRUNK_ZERO: Italy +39). */
export const KEEPS_TRUNK_ZERO = ['+39']

export function normalizePhone(input: string): { value: string; note?: string } {
  let v = input.replace(/[\s\-()]/g, '')
  if (!v.startsWith('+')) return { value: v, note: 'Include your country code, e.g. +971…' }
  for (const code of ['+971', '+44', '+1', '+39', '+33', '+49', '+34', '+61', '+64', '+27', '+91', '+86', '+81']) {
    if (v.startsWith(code)) {
      const rest = v.slice(code.length)
      if (rest.startsWith('0') && !KEEPS_TRUNK_ZERO.includes(code)) {
        // Note copy is the canon §5.3 verbatim string.
        return { value: code + rest.slice(1), note: 'Leading 0 removed — numbers are stored international' }
      }
      return { value: v }
    }
  }
  return { value: v }
}

export async function getProfile(accessToken: string, fetchImpl: typeof fetch = fetch): Promise<CognitoProfile> {
  const json = (await call(TARGETS.get, { AccessToken: accessToken }, fetchImpl)) as { UserAttributes: AttrList }
  return attrsToProfile(json.UserAttributes ?? [])
}

export async function updateProfile(
  accessToken: string,
  p: CognitoProfile,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  await call(TARGETS.update, { AccessToken: accessToken, UserAttributes: profileToAttrs(p) }, fetchImpl)
}
